import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tabs,
  Switch,
  message,
  Tag,
  Checkbox,
  Row,
  Col,
  Alert,
  Spin,
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  LockOutlined,
  UserOutlined,
  TeamOutlined,
  CrownOutlined,
  EyeOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { menuService, MenuWithPermissions, MenuPermission, RoleType } from '../services/menuService';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

interface PermissionRow {
  menuId: string;
  menuName: string;
  menuPath: string;
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

const MenuPermissions: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<RoleType>('admin');
  const [menus, setMenus] = useState<MenuWithPermissions[]>([]);
  const [permissions, setPermissions] = useState<Record<RoleType, PermissionRow[]>>({
    super_admin: [],
    admin: [],
    user: [],
  });
  const [modifiedPermissions, setModifiedPermissions] = useState<Record<RoleType, Set<string>>>({
    super_admin: new Set(),
    admin: new Set(),
    user: new Set(),
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // 메뉴 목록 조회
      const menuData = await menuService.getMenus(true);
      setMenus(menuData);

      // 권한 정보 조회
      const permissionData = await menuService.getPermissions();
      
      // 역할별로 권한 정보 정리
      const rolePermissions: Record<RoleType, PermissionRow[]> = {
        super_admin: [],
        admin: [],
        user: [],
      };

      // 메뉴별로 권한 정보 매핑
      const flatMenus = flattenMenus(menuData);
      
      flatMenus.forEach(menu => {
        (['super_admin', 'admin', 'user'] as RoleType[]).forEach(role => {
          const permission = permissionData.find(
            p => p.menu_id === menu.id && p.role === role
          );

          rolePermissions[role].push({
            menuId: menu.id,
            menuName: menu.name,
            menuPath: menu.path || '',
            canView: permission?.can_view || false,
            canCreate: permission?.can_create || false,
            canUpdate: permission?.can_update || false,
            canDelete: permission?.can_delete || false,
          });
        });
      });

      setPermissions(rolePermissions);
    } catch (error) {
      message.error('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const flattenMenus = (menus: MenuWithPermissions[]): MenuWithPermissions[] => {
    const result: MenuWithPermissions[] = [];
    const traverse = (menuList: MenuWithPermissions[]) => {
      menuList.forEach(menu => {
        result.push(menu);
        if (menu.children) {
          traverse(menu.children);
        }
      });
    };
    traverse(menus);
    return result;
  };

  const handlePermissionChange = (
    role: RoleType,
    menuId: string,
    permissionType: 'canView' | 'canCreate' | 'canUpdate' | 'canDelete',
    checked: boolean
  ) => {
    setPermissions(prev => ({
      ...prev,
      [role]: prev[role].map(p =>
        p.menuId === menuId
          ? {
              ...p,
              [permissionType]: checked,
              // 보기 권한이 없으면 다른 권한도 모두 제거
              ...(permissionType === 'canView' && !checked
                ? { canCreate: false, canUpdate: false, canDelete: false }
                : {}),
              // 다른 권한을 체크하면 보기 권한도 자동으로 체크
              ...(permissionType !== 'canView' && checked
                ? { canView: true }
                : {}),
            }
          : p
      ),
    }));

    // 수정된 권한 추적
    setModifiedPermissions(prev => ({
      ...prev,
      [role]: new Set([...Array.from(prev[role] || new Set()), menuId]),
    }));
  };

  const handleSelectAll = (role: RoleType, permissionType: 'canView' | 'canCreate' | 'canUpdate' | 'canDelete') => {
    const allChecked = permissions[role].every(p => p[permissionType]);
    
    setPermissions(prev => ({
      ...prev,
      [role]: prev[role].map(p => ({
        ...p,
        [permissionType]: !allChecked,
        // 보기 권한을 해제하면 다른 권한도 모두 해제
        ...(permissionType === 'canView' && allChecked
          ? { canCreate: false, canUpdate: false, canDelete: false }
          : {}),
        // 다른 권한을 체크하면 보기 권한도 자동으로 체크
        ...(permissionType !== 'canView' && !allChecked
          ? { canView: true }
          : {}),
      })),
    }));

    // 모든 메뉴를 수정된 것으로 표시
    setModifiedPermissions(prev => ({
      ...prev,
      [role]: new Set(permissions[role].map(p => p.menuId)),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // 수정된 권한만 저장
      const updates: Array<{ menu_id: string; can_view: boolean; can_create: boolean; can_update: boolean; can_delete: boolean }> = [];
      
      modifiedPermissions[activeTab].forEach(menuId => {
        const permission = permissions[activeTab].find(p => p.menuId === menuId);
        if (permission) {
          updates.push({
            menu_id: menuId,
            can_view: permission.canView,
            can_create: permission.canCreate,
            can_update: permission.canUpdate,
            can_delete: permission.canDelete,
          });
        }
      });

      if (updates.length > 0) {
        await menuService.updateRolePermissions(activeTab, updates);
        message.success('권한이 저장되었습니다.');
        
        // 수정 사항 초기화
        setModifiedPermissions(prev => ({
          ...prev,
          [activeTab]: new Set(),
        }));
      } else {
        message.info('변경된 권한이 없습니다.');
      }
    } catch (error) {
      message.error('권한 저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: '메뉴명',
      dataIndex: 'menuName',
      key: 'menuName',
      width: 200,
    },
    {
      title: '경로',
      dataIndex: 'menuPath',
      key: 'menuPath',
      width: 200,
    },
    {
      title: (
        <Space>
          <EyeOutlined />
          보기
          <Checkbox
            onChange={() => handleSelectAll(activeTab, 'canView')}
            checked={permissions[activeTab].every(p => p.canView)}
            indeterminate={
              permissions[activeTab].some(p => p.canView) &&
              !permissions[activeTab].every(p => p.canView)
            }
          />
        </Space>
      ),
      dataIndex: 'canView',
      key: 'canView',
      width: 120,
      align: 'center' as const,
      render: (value: boolean, record: PermissionRow) => (
        <Checkbox
          checked={value}
          onChange={(e: CheckboxChangeEvent) =>
            handlePermissionChange(activeTab, record.menuId, 'canView', e.target.checked)
          }
          disabled={activeTab === 'super_admin'}
        />
      ),
    },
    {
      title: (
        <Space>
          <PlusOutlined />
          생성
          <Checkbox
            onChange={() => handleSelectAll(activeTab, 'canCreate')}
            checked={permissions[activeTab].every(p => p.canCreate)}
            indeterminate={
              permissions[activeTab].some(p => p.canCreate) &&
              !permissions[activeTab].every(p => p.canCreate)
            }
          />
        </Space>
      ),
      dataIndex: 'canCreate',
      key: 'canCreate',
      width: 120,
      align: 'center' as const,
      render: (value: boolean, record: PermissionRow) => (
        <Checkbox
          checked={value}
          onChange={(e: CheckboxChangeEvent) =>
            handlePermissionChange(activeTab, record.menuId, 'canCreate', e.target.checked)
          }
          disabled={activeTab === 'super_admin'}
        />
      ),
    },
    {
      title: (
        <Space>
          <EditOutlined />
          수정
          <Checkbox
            onChange={() => handleSelectAll(activeTab, 'canUpdate')}
            checked={permissions[activeTab].every(p => p.canUpdate)}
            indeterminate={
              permissions[activeTab].some(p => p.canUpdate) &&
              !permissions[activeTab].every(p => p.canUpdate)
            }
          />
        </Space>
      ),
      dataIndex: 'canUpdate',
      key: 'canUpdate',
      width: 120,
      align: 'center' as const,
      render: (value: boolean, record: PermissionRow) => (
        <Checkbox
          checked={value}
          onChange={(e: CheckboxChangeEvent) =>
            handlePermissionChange(activeTab, record.menuId, 'canUpdate', e.target.checked)
          }
          disabled={activeTab === 'super_admin'}
        />
      ),
    },
    {
      title: (
        <Space>
          <DeleteOutlined />
          삭제
          <Checkbox
            onChange={() => handleSelectAll(activeTab, 'canDelete')}
            checked={permissions[activeTab].every(p => p.canDelete)}
            indeterminate={
              permissions[activeTab].some(p => p.canDelete) &&
              !permissions[activeTab].every(p => p.canDelete)
            }
          />
        </Space>
      ),
      dataIndex: 'canDelete',
      key: 'canDelete',
      width: 120,
      align: 'center' as const,
      render: (value: boolean, record: PermissionRow) => (
        <Checkbox
          checked={value}
          onChange={(e: CheckboxChangeEvent) =>
            handlePermissionChange(activeTab, record.menuId, 'canDelete', e.target.checked)
          }
          disabled={activeTab === 'super_admin'}
        />
      ),
    },
  ];

  const tabItems = [
    {
      key: 'super_admin',
      label: (
        <Space>
          <CrownOutlined />
          슈퍼관리자
        </Space>
      ),
      children: (
        <div>
          <Alert
            message="슈퍼관리자는 모든 메뉴에 대한 전체 권한을 가지며 변경할 수 없습니다."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <Table
            columns={columns}
            dataSource={permissions.super_admin}
            rowKey="menuId"
            pagination={false}
            size="middle"
          />
        </div>
      ),
    },
    {
      key: 'admin',
      label: (
        <Space>
          <TeamOutlined />
          일반관리자
          {modifiedPermissions.admin.size > 0 && (
            <Tag color="orange">{modifiedPermissions.admin.size} 수정</Tag>
          )}
        </Space>
      ),
      children: (
        <Table
          columns={columns}
          dataSource={permissions.admin}
          rowKey="menuId"
          pagination={false}
          size="middle"
        />
      ),
    },
    {
      key: 'user',
      label: (
        <Space>
          <UserOutlined />
          일반사용자
          {modifiedPermissions.user.size > 0 && (
            <Tag color="orange">{modifiedPermissions.user.size} 수정</Tag>
          )}
        </Space>
      ),
      children: (
        <Table
          columns={columns}
          dataSource={permissions.user}
          rowKey="menuId"
          pagination={false}
          size="middle"
        />
      ),
    },
  ];

  return (
    <div>
      <h1>메뉴 권한 관리</h1>
      
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Row justify="space-between">
            <Col>
              <Alert
                message="메뉴별 권한을 역할에 따라 설정할 수 있습니다."
                type="info"
                showIcon
                style={{ display: 'inline-block' }}
              />
            </Col>
            <Col>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadData}
                  loading={loading}
                >
                  새로고침
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={saving}
                  disabled={modifiedPermissions[activeTab].size === 0}
                >
                  저장 ({modifiedPermissions[activeTab].size}개 변경)
                </Button>
              </Space>
            </Col>
          </Row>
        </div>

        <Spin spinning={loading}>
          <Tabs
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key as RoleType)}
            items={tabItems}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default MenuPermissions;